import React, {memo} from 'react'
import {useIntl} from 'react-intl'
import styled from '@emotion/styled'
import {useFiltersForm} from 'hooks/useFiltersForm'
import {FilterUrlParams} from 'modules/search/types'

import {Checkbox, CheckboxBody, CheckboxContent} from '@vio/atlas/Checkbox'
import {Box} from '@vio/atlas/components/designSystem/helpers/Box'
import {Text} from '@vio/atlas/components/designSystem/Text'
import {cssTheme} from '@vio/atlas/themes'
import minus from '@vio/atlas/utils/minus'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

const Wrapper = styled.div`
  display: grid;
  grid-gap: ${cssTheme.layout.spacing.s500};
`

const StyledCheckBox = styled(Checkbox)`
  ${CheckboxBody} {
    align-items: flex-start;
  }

  ${CheckboxContent} {
    margin-top: ${minus(cssTheme.layout.spacing.s100)};
  }
`

interface Props
  extends Pick<ReturnType<typeof useFiltersForm>, 'toggleFilters'> {
  filters: FilterUrlParams
}

const ExclusiveFilterComponent: React.FC<Props> = ({
  filters,
  toggleFilters
}) => {
  const {formatMessage} = useIntl()
  const {brandName} = useBrand()
  const {
    monetisation: {onlyOnBrandTag}
  } = useBrandCapabilities()
  const isExclusiveFilterActive = filters.exclusive === '1'

  // Parent only renders this filter when the on-brand tag is enabled, but
  // the discriminated union forces the check before reading config.message.
  if (!onlyOnBrandTag.enabled) return null

  return (
    <Wrapper>
      <StyledCheckBox
        key="exclusive"
        name="exclusive"
        value="exclusive"
        checked={isExclusiveFilterActive}
        onChange={() =>
          toggleFilters({
            exclusive: isExclusiveFilterActive ? undefined : '1'
          })
        }
        label={
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="flex-end"
            marginBottom="s200"
          >
            <Text variant="bodyM">
              {formatMessage(onlyOnBrandTag.config.message, {brandName})}
            </Text>
            <Text variant="bodyS">
              <FormattedMessageWrapper
                id="onlyOnVio.description"
                defaultMessage="Exclusive deals you won't find anywhere else"
              />
            </Text>
          </Box>
        }
      />
    </Wrapper>
  )
}

export const ExclusiveFilter = memo(ExclusiveFilterComponent)
